import { computed } from '@angular/core';
import { validateStandardSchema } from '@angular/forms/signals';

import type { IndentEntity } from '@sites/entity-core';
import { DefaultIndentEntitySchema } from '@sites/entity-core';

import type { BaseEntityFormOptions } from '../../core/create-base-entity-form';
import { configureVersionedReadonly, createBaseEntityForm } from '../../core/create-base-entity-form';
import { composeForm } from '../../core/compose-form';
import { widgetEnvelopePlugin } from '../../plugins/widget/widget-envelope.plugin';

export type IndentFormModel = IndentEntity;
export type IndentPropsModel = IndentEntity['props'];

type IndentHeight = IndentPropsModel['height'];

/**
 * Signal-form view of the indent (spacer) widget at its latest version, over the versioned base form.
 * Its whole settings surface is one height per breakpoint plus the flag that keeps
 * them in sync.
 */
export function createIndentForm(options?: BaseEntityFormOptions<IndentEntity>) {
    const base = createBaseEntityForm<IndentEntity>((schemaPath) => {
        validateStandardSchema(schemaPath, DefaultIndentEntitySchema.latestSchema);
        configureVersionedReadonly(schemaPath);
    }, options);

    const _raw = base._raw;

    // Through the `height` node: the `tablet` / `mobile` leaves are optional, so on a spacer persisted
    // without one its child node does not exist and `_raw.props.height.mobile()` would throw.
    const writeHeight = (leaves: Partial<IndentHeight>) => {
        _raw.props.height().value.update((height) => ({ ...height, ...leaves }));
    };

    return composeForm(
        base,
        widgetEnvelopePlugin(() => _raw),
        {
            get height() {
                return _raw.props.height.desktop;
            },
            /** The whole per-device node — read its value for the optional `tablet` / `mobile` leaves. */
            get heights() {
                return _raw.props.height;
            },
            /** The height the tablet tab shows — what the stylesheet renders there (tablet → mobile → desktop). */
            tabletHeightValue: computed<number>(() => {
                const height = _raw.props.height().value();
                return height.tablet ?? height.mobile ?? height.desktop;
            }),
            setTabletHeight: (value: IndentHeight['desktop']) => writeHeight({ tablet: value }),

            /** The height the mobile tab shows — the desktop one while the spacer has no `mobile` leaf. */
            mobileHeightValue: computed<number>(() => {
                const height = _raw.props.height().value();
                return height.mobile ?? height.desktop;
            }),
            setMobileHeight: (value: IndentHeight['desktop']) => writeHeight({ mobile: value }),

            get syncWithDesktop() {
                return _raw.props.syncWithDesktop;
            },
            isSyncedWithDesktop: computed<boolean>(() => _raw.props.syncWithDesktop().value()),
            setSyncWithDesktop: (value: boolean) => {
                _raw.props.syncWithDesktop().value.set(value);
            },

            /** Desktop height, mirrored onto tablet and mobile while synchronised — one write. */
            setHeight: (value: IndentHeight['desktop']) => {
                writeHeight(
                    _raw.props.syncWithDesktop().value()
                        ? { desktop: value, tablet: value, mobile: value }
                        : { desktop: value }
                );
            },
        }
    );
}

export type IndentForm = ReturnType<typeof createIndentForm>;
